import request from '@/api/request'

// 响应解包约定（与项目其他模块一致）：拦截器返回完整 axios response，
// 业务数据在 res.data.data，本文件统一在 API 层解包，使用处直接拿到业务数据
const unwrap = (res: any) => res.data.data

export interface AiStatus {
  enabled: boolean // 实际可用（启用且免费或有余额），欠费时前端 AI 入口自动隐藏
  configured: boolean
  free: boolean // 免费租户（平台自有）
  balance: number
  canUse: boolean
  canConfig: boolean
  canGrowth: boolean // 是否有 ai:growth 权限
  growth: { enabled: boolean } // 当前版本是否包含 AI获客功能
  asr: boolean // 语音输入是否可用（开启且已配置密钥）
}

export function getAiStatus(): Promise<AiStatus> {
  return request.get('/ai/status').then(unwrap)
}

// 平台模型清单条目（租户视角：价格为售价=成本×加价率，Key 不下发）
export interface AiPlatformModel {
  id: number
  name: string
  type: 'text' | 'vision' | 'asr'
  provider: string
  model: string
  inputPrice: number // 售价 元/百万token
  outputPrice: number
  pricePerCall: number // ASR 每次售价
  selected: boolean
  isDefault: boolean
}

export interface AiConfig {
  enabled: boolean
  free: boolean
  balance: number
  totalRecharged: number
  totalConsumed: number
  logAnswer: boolean // 是否在审计日志中记录 AI 回答内容
  markup: number // 平台加价率（展示用）
  models: AiPlatformModel[]
}

export function getAiConfig(): Promise<AiConfig> {
  return request.get('/ai/config').then(unwrap)
}

export interface AiConfigSavePayload {
  enabled?: boolean
  logAnswer?: boolean
  selectedModelIds?: number[]
  defaults?: { text?: number; vision?: number; asr?: number }
}

export function saveAiConfig(data: AiConfigSavePayload) {
  return request.put('/ai/config', data)
}

export function testAiConfig(): Promise<{ reply: string; model: string }> {
  return request.post('/ai/config/test').then(unwrap)
}

// ===== 消费流水与充值（消费透明） =====
export interface AiBillItem {
  id: number
  modelId: number | null
  modelName: string
  type: string
  userId: number | null
  username: string
  inputTokens: number
  outputTokens: number
  cost: number
  price: number // 本条扣费（元）
  balanceAfter: number | null
  question: string | null
  createdAt: string
}

export function getAiBills(params: { page?: number; pageSize?: number }): Promise<{ list: AiBillItem[]; pagination: { page: number; pageSize: number; total: number } }> {
  return request.get('/ai/bills', { params }).then(unwrap)
}

export interface AiRechargeItem {
  id: number
  amount: number
  method: 'manual' | 'wechat'
  status: string
  operatorName: string
  remark: string
  createdAt: string
}

export function getAiRecharges(): Promise<AiRechargeItem[]> {
  return request.get('/ai/recharges').then(unwrap)
}

// 微信在线充值（预留，未上线时后端返回 501）
export function rechargeByWechat(amount: number) {
  return request.post('/ai/recharge/wechat', { amount })
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface AiChatResult {
  reply: string
  model: string
  toolsUsed?: string[]
  knowledgeHits?: number
  conversationId?: number | null
}

// 通用对话：conversationId 存在时后端以库内历史为准（落库会话），否则用 history 并新建会话
export function aiChat(message: string, history: ChatMessage[] = [], conversationId?: number | null): Promise<AiChatResult> {
  return request.post('/ai/chat', { message, history, conversationId: conversationId ?? null }, { timeout: 90000 }).then(unwrap)
}

// ===== AI 会话（落库，2026-11-04；本人会话 ai:use，跨人查看需 ai:conversations:all） =====
export interface AiConversation {
  id: number
  userId: number
  username: string
  title: string
  messageCount: number
  createdAt: string
  updatedAt: string
}

export interface AiConversationQuery {
  page?: number
  pageSize?: number
  username?: string
  keyword?: string
  dateFrom?: string
  dateTo?: string
}

export function getAiConversations(params?: AiConversationQuery): Promise<{ list: AiConversation[]; pagination: { page: number; pageSize: number; total: number }; canViewAll: boolean }> {
  return request.get('/ai/conversations', { params }).then(unwrap)
}

export interface AiConversationMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  model: string | null
  toolsUsed: string | null
  knowledgeHits: number
  createdAt: string
}

export function getAiConversationDetail(id: number): Promise<AiConversation & { messages: AiConversationMessage[] }> {
  return request.get(`/ai/conversations/${id}`).then(unwrap)
}

export function deleteAiConversation(id: number) {
  return request.delete(`/ai/conversations/${id}`)
}

// ===== 语音转文字 =====
export function aiAsr(audio: Blob, filename: string): Promise<{ text: string; provider: string }> {
  const formData = new FormData()
  formData.append('audio', audio, filename)
  return request.post('/ai/asr', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000
  }).then(unwrap)
}

// ===== 语音/文字指令解析（创建待办/安装工单/客户跟进） =====
export interface AiCandidateCustomer {
  id: number
  name: string
  phone: string
  address: string
}

export interface AiCandidateEmployee {
  id: number
  name: string
}

export type AiCommandIntent = 'create_todo' | 'create_work_order' | 'create_follow_up' | 'create_payment' | 'create_expense' | 'none'

export interface AiCandidateSupplier {
  id: number
  name: string
  contactName?: string
  phone?: string
}

export interface AiCandidateAccount {
  id: number
  name: string
  type: 'cash' | 'bank' | 'wechat' | 'alipay'
}

export interface AiCandidateExpenseCategory {
  id: number
  name: string
  parentId?: number | null
}

export interface AiCommandResult {
  intent: AiCommandIntent
  reply: string
  params: any
  candidates?: {
    customers?: AiCandidateCustomer[]
    installers?: AiCandidateEmployee[]
    installerOptions?: AiCandidateEmployee[]
    assignees?: AiCandidateEmployee[]
    suppliers?: AiCandidateSupplier[]
    accounts?: AiCandidateAccount[]
    expenseCategories?: AiCandidateExpenseCategory[]
  }
  self?: {
    userId: number
    employeeId: number | null
    employeeName: string | null
  }
}

export function parseAiCommand(message: string, history: ChatMessage[] = []): Promise<AiCommandResult> {
  return request.post('/ai/parse-command', { message, history }, { timeout: 90000 }).then(unwrap)
}

// ===== 票据图片识别（收付款记录/报销单草稿，确认后才创建） =====
export interface AiVoucherParams {
  voucherType: 'receive' | 'pay' | 'expense' | 'unknown'
  amount: number
  counterpartyName: string
  paymentMethod: 'cash' | 'transfer' | 'check' | 'wechat' | 'alipay' | null
  transactionNo: string
  paymentDate: string | null
  summary: string
  reply: string
}

export interface AiVoucherResult {
  intent: 'create_payment' | 'create_expense'
  reply: string
  params: AiVoucherParams
  candidates?: AiCommandResult['candidates']
  self?: AiCommandResult['self']
}

export function parseAiVoucher(image: File, text?: string): Promise<AiVoucherResult> {
  const formData = new FormData()
  formData.append('image', image)
  if (text) formData.append('text', text)
  return request.post('/ai/parse-voucher', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  }).then(unwrap)
}

export interface QinsilkParseItem {
  productName: string
  productCode: string
  barcode: string
  unit: string
  quantity: number
  unitPrice: number
  amount: number
}

export interface QinsilkParseResult {
  orderNo: string
  orderType: '销售单' | '销售退货单' | '采购单' | '采购退货单'
  orderDate: string | null
  customerName: string
  supplierName: string
  totalAmount: number
  remark: string
  items: QinsilkParseItem[]
  duplicated: boolean
  duplicates: { id: number; orderNo: string; remark: string; source: string }[]
  warning?: string
}

// 秦丝单据智能解析：text 和 imageFile 至少传一个
// perspective='purchase'：采购单表单使用，告知 AI 按采购方视角解析（供货商开的销售单视为采购单）
export function parseQinsilk(text: string, imageFile?: File, perspective?: 'purchase'): Promise<QinsilkParseResult> {
  const formData = new FormData()
  if (text) formData.append('text', text)
  if (imageFile) formData.append('image', imageFile)
  if (perspective) formData.append('perspective', perspective)
  return request.post('/ai/parse-qinsilk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  }).then(unwrap)
}

export interface QinsilkReturnImportResult {
  returnId: number
  returnNo: string
  totalAmount: number
  itemCount: number
  customerCreated: boolean
  createdProducts: string[]
}

// 秦丝退货单直接创建草稿（解析确认后调用；销售/采购单请走表单回填）
export function importQinsilkReturn(data: QinsilkParseResult & { items: (QinsilkParseItem & { productId?: number | null })[] }): Promise<QinsilkReturnImportResult> {
  return request.post('/ai/import-qinsilk-return', data).then(unwrap)
}

export interface AiUsage {
  days: number
  summary: {
    totalCalls: number
    totalTokens: number
    totalCost: number
    failedCalls: number
    activeUsers: number
  }
  byType: { type: string; calls: number; tokens: number; cost: number }[]
  byUser: { username: string; calls: number; tokens: number; cost: number }[]
  byDay: { day: string; calls: number; cost: number }[]
}

export function getAiUsage(days = 30): Promise<AiUsage> {
  return request.get('/ai/usage', { params: { days } }).then(unwrap)
}

export interface AiLogItem {
  id: number
  userId: number
  username: string
  type: 'chat' | 'parse_qinsilk'
  provider: string
  model: string
  promptTokens: number
  completionTokens: number
  totalTokens: number
  cost: number
  question: string | null
  answer: string | null
  status: 'success' | 'failed' | 'denied'
  errorMessage: string | null
  createdAt: string
}

export interface AiLogQuery {
  page?: number
  pageSize?: number
  username?: string
  type?: string
  days?: number
}

// AI 调用明细（审计日志，仅 ai:config 权限）
export function getAiLogs(params?: AiLogQuery): Promise<{ list: AiLogItem[]; pagination: { page: number; pageSize: number; total: number } }> {
  return request.get('/ai/logs', { params }).then(unwrap)
}

// AI 生成打印模板：按自然语言描述生成/修改 Mustache 打印模板（返回的 html 需人工预览确认后保存）
export function generatePrintTemplate(docType: string, instruction: string, currentHtml?: string): Promise<{ html: string; model: string }> {
  return request.post('/ai/print-template', { docType, instruction, currentHtml }, { timeout: 120000 }).then(unwrap)
}

// AI 图片生成打印模板：上传样单照片/截图，视觉模型还原版式生成 Mustache 模板（instruction 为可选补充说明）
export function generatePrintTemplateFromImage(docType: string, image: File, instruction?: string): Promise<{ html: string; model: string }> {
  const formData = new FormData()
  formData.append('image', image)
  formData.append('docType', docType)
  if (instruction) formData.append('instruction', instruction)
  return request.post('/ai/print-template-from-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000
  }).then(unwrap)
}

// ===== AI 知识库（租户独立知识，ai:config 管理；对话时与平台共享知识合并向量检索） =====
export interface AiKnowledgeDoc {
  id: number
  title: string
  category: string | null
  sourceType: 'manual' | 'file'
  content?: string | null
  fileName: string | null
  mimeType: string | null
  fileSize: number | null
  status: 'processing' | 'ready' | 'failed'
  failReason: string | null
  chunkCount: number
  charCount: number
  isActive: 0 | 1
  remark: string | null
  createdBy: number | null
  createdAt: string
  updatedAt: string
}

export interface AiKnowledgeChunk {
  id: number
  chunkIndex: number
  content: string
  vectorized: 0 | 1
}

export function getKnowledgeList(params: { page?: number; pageSize?: number; keyword?: string; category?: string }): Promise<{ list: AiKnowledgeDoc[]; pagination: { page: number; pageSize: number; total: number } }> {
  return request.get('/ai/knowledge', { params }).then(unwrap)
}

export function getKnowledgeCategories(): Promise<string[]> {
  return request.get('/ai/knowledge/categories').then(unwrap)
}

export function getKnowledgeDetail(id: number): Promise<AiKnowledgeDoc & { chunks: AiKnowledgeChunk[] }> {
  return request.get(`/ai/knowledge/${id}`).then(unwrap)
}

export function createKnowledge(data: { title: string; content: string; category?: string; remark?: string }): Promise<{ id: number; status: string }> {
  return request.post('/ai/knowledge', data).then(unwrap)
}

// 文件学习：txt/md/csv/xlsx/docx/pdf 文档或 jpg/png/webp/gif 图片（图片走视觉模型提取）
export function uploadKnowledgeFile(file: File, title?: string, category?: string, remark?: string): Promise<{ id: number; status: string }> {
  const formData = new FormData()
  formData.append('file', file)
  if (title) formData.append('title', title)
  if (category) formData.append('category', category)
  if (remark) formData.append('remark', remark)
  return request.post('/ai/knowledge/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000
  }).then(unwrap)
}

export function updateKnowledge(id: number, data: { title?: string; content?: string; category?: string | null; remark?: string | null; isActive?: 0 | 1 }) {
  return request.put(`/ai/knowledge/${id}`, data)
}

export function rebuildKnowledge(id: number): Promise<{ status: string }> {
  return request.post(`/ai/knowledge/${id}/rebuild`).then(unwrap)
}

export function deleteKnowledge(id: number) {
  return request.delete(`/ai/knowledge/${id}`)
}
